import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Bell, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useFamily } from '@/context/FamilyContext';
import { daysUntil, urgencyClass, urgencyDot, urgencyLabel, formatDate, computeNextDueDate, toISODate } from '@/lib/dateUtils';
import { parseISO } from 'date-fns';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { ServiceDoneModal } from '@/components/ServiceDoneModal';
import { TopBar } from '@/components/TopBar';
import type { Reminder, Item, DashboardItem } from '@/types';

export function Dashboard() {
  const { user } = useAuth();
  const { family, canEdit } = useFamily();
  const navigate = useNavigate();
  const [dashItems, setDashItems] = useState<DashboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeDone, setActiveDone] = useState<Reminder | null>(null);

  const load = useCallback(async () => {
    if (!family) return;
    setLoading(true);

    const { data: reminders } = await supabase
      .from('reminders')
      .select('*')
      .eq('family_id', family.id)
      .eq('is_active', true);

    if (!reminders) { setLoading(false); return; }

    // Filter to within 60 days and overdue
    const relevant = (reminders as Reminder[]).filter((r) => daysUntil(r.next_due_date) <= 60);
    if (relevant.length === 0) { setDashItems([]); setLoading(false); return; }

    const itemIds = [...new Set(relevant.map((r) => r.item_id))];
    const { data: itemsData } = await supabase
      .from('items')
      .select('*')
      .in('id', itemIds);

    const itemMap = new Map<string, Item>((itemsData ?? []).map((i) => [i.id, i as Item]));

    const di: DashboardItem[] = relevant
      .map((r) => ({
        reminder: r,
        item: itemMap.get(r.item_id)!,
        daysUntilDue: daysUntil(r.next_due_date),
        isOverdue: daysUntil(r.next_due_date) < 0
      }))
      .filter((d) => d.item)
      .sort((a, b) => a.daysUntilDue - b.daysUntilDue);

    setDashItems(di);
    setLoading(false);
  }, [family]);

  useEffect(() => { load(); }, [load]);

  // Refresh on window focus
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const quickDone = async (reminder: Reminder) => {
    const today = toISODate(new Date());
    const { error } = await supabase.from('service_logs').insert({
      reminder_id: reminder.id,
      item_id: reminder.item_id,
      family_id: reminder.family_id,
      done_date: today,
      created_by: user!.id
    });
    if (error) { toast.error(error.message); return; }

    if (reminder.one_time) {
      await supabase.from('reminders').update({ is_active: false }).eq('id', reminder.id);
    } else {
      const next = computeNextDueDate(parseISO(reminder.next_due_date), reminder.repeat_every, reminder.repeat_unit);
      await supabase.from('reminders').update({ next_due_date: toISODate(next), last_notified_for: null }).eq('id', reminder.id);
    }

    toast.success('Marked done!');
    load();
  };

  const overdue = dashItems.filter((d) => d.isOverdue);
  const upcoming = dashItems.filter((d) => !d.isOverdue);

  return (
    <div>
      <TopBar
        title="Dashboard"
        right={
          <div className="text-xs text-gray-400 font-normal">{family?.name}</div>
        }
      />
      <div className="px-4 py-4 space-y-6">
        {loading && <LoadingSpinner className="py-12" />}

        {!loading && dashItems.length === 0 && (
          <EmptyState
            icon={<Bell className="w-7 h-7" />}
            title="All clear!"
            description="No reminders due in the next 60 days. Add items to start tracking."
          />
        )}

        {!loading && overdue.length > 0 && (
          <Section title="Overdue" color="text-red-600" icon={<AlertTriangle className="w-4 h-4" />}>
            {overdue.map((d) => (
              <DashCard key={d.reminder.id} d={d} canEdit={canEdit} onQuickDone={quickDone} onDetails={() => navigate(`/items/${d.item.id}`)} onServiceDone={setActiveDone} />
            ))}
          </Section>
        )}

        {!loading && upcoming.length > 0 && (
          <Section title="Upcoming (60 days)" color="text-gray-700">
            {upcoming.map((d) => (
              <DashCard key={d.reminder.id} d={d} canEdit={canEdit} onQuickDone={quickDone} onDetails={() => navigate(`/items/${d.item.id}`)} onServiceDone={setActiveDone} />
            ))}
          </Section>
        )}
      </div>

      {activeDone && user && (
        <ServiceDoneModal
          reminder={activeDone}
          userId={user.id}
          onClose={() => setActiveDone(null)}
          onDone={() => { setActiveDone(null); load(); }}
        />
      )}
    </div>
  );
}

function Section({ title, color, icon, children }: { title: string; color: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className={`flex items-center gap-1.5 mb-3 ${color}`}>
        {icon}
        <h2 className="font-semibold text-sm">{title}</h2>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function DashCard({ d, canEdit, onQuickDone, onDetails, onServiceDone }: {
  d: DashboardItem;
  canEdit: boolean;
  onQuickDone: (r: Reminder) => void;
  onDetails: () => void;
  onServiceDone: (r: Reminder) => void;
}) {
  const cls = urgencyClass(d.daysUntilDue);
  const dot = urgencyDot(d.daysUntilDue);
  const lbl = urgencyLabel(d.daysUntilDue);

  return (
    <div className={`rounded-xl border p-4 bg-white shadow-sm ${cls} cursor-pointer`} onClick={onDetails}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
            <p className="text-sm font-semibold text-gray-900 truncate">{d.reminder.title}</p>
          </div>
          <p className="text-xs text-gray-500 ml-4">{d.item.name} · {formatDate(d.reminder.next_due_date)}</p>
          <p className={`text-xs font-medium ml-4 mt-0.5`}>{lbl}</p>
        </div>
        {canEdit && (
          <div className="flex gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onQuickDone(d.reminder)}
              className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 hover:text-emerald-600 transition-colors"
              title="Quick mark done"
            >
              <CheckCircle2 className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
